import { useCallback, useMemo } from "react";

import { getFieldOutcomeBasedPayoutRange, useGetIncentivesPotentialPayoutsAdmin, useGetIncentivesPotentialPayoutsByFarmId } from "utils";

import { CarbonProgram, IncentiveName, IncentivesPotentialPayouts, SingleFieldOutcomes } from "models";

import {
  CurrentIncentivePayouts,
  aggregateIncentiveCurrentPayouts,
  aggregatePracticeBasedIncentiveCurrentPayouts,
} from "pages/app/field-timeline/components/PracticeBasedImpact";

export interface InventoryImpact {
  projectAreaHa: number;
  maximumIncentivePerHectare?: number | null; // undefined for intervention, null or number for inventory
  maximumPotentialPayout: number | null;
  totalOutcomeBasedPayoutMin: number;
  totalOutcomeBasedPayoutMax: number;
  totalPracticeBasedPayout: number;
  totalPayoutMin: number;
  totalPayoutMax: number;
  isMaxTotalPayoutReached: boolean;
  totalOutcomePotentialPayout: number;
  isMaxOutcomePayoutReached: boolean;
  hideInventoryPayouts: boolean;
  incentivesCurrentPayouts: CurrentIncentivePayouts;
  allPracticesMaxedOut: boolean;
  isPracticePotentialFullyUtilized: boolean;
  practicePotentialPayoutTotal: number;
  incentivesPotentialPayouts: IncentivesPotentialPayouts | undefined;
  displayPerHectareOrTotalFarm: (value: number, isPerHectare: boolean) => number;
}

export function useInventoryImpactAdmin(
  reportingYear: number,
  singleFieldOutcomes: SingleFieldOutcomes[],
  farmId: string,
  carbonProgram: CarbonProgram | undefined
): InventoryImpact {
  const { incentivesPotentialPayouts } = useGetIncentivesPotentialPayoutsAdmin(carbonProgram ? farmId : undefined);

  return useInventoryImpactShared(reportingYear, singleFieldOutcomes, carbonProgram, incentivesPotentialPayouts);
}
export function useInventoryImpact(
  reportingYear: number,
  singleFieldOutcomes: SingleFieldOutcomes[],
  farmId: string,
  carbonProgram: CarbonProgram | undefined
): InventoryImpact {
  const { incentivesPotentialPayouts } = useGetIncentivesPotentialPayoutsByFarmId(carbonProgram?.accountingMethodology === "inventory" ? farmId : undefined);

  return useInventoryImpactShared(reportingYear, singleFieldOutcomes, carbonProgram, incentivesPotentialPayouts);
}

// For lack of a better naming this is the function that is reused for both user and admin hook
function useInventoryImpactShared(
  reportingYear: number,
  singleFieldOutcomes: SingleFieldOutcomes[],
  carbonProgram: CarbonProgram | null | undefined,
  incentivesPotentialPayouts: IncentivesPotentialPayouts | undefined
) {
  const projectAreaHa = incentivesPotentialPayouts?.projectAreaHa ?? 0;
  const practicePotentialPayoutTotal = incentivesPotentialPayouts?.practicePotentialTotalPayout ?? 0;
  const maximumIncentivePerHectare = carbonProgram?.fieldsManagement.maximumPayoutPerHa;
  const maximumPotentialPayout = typeof maximumIncentivePerHectare === "number" ? maximumIncentivePerHectare * projectAreaHa : null;

  // per hectare or total payout
  const displayPerHectareOrTotalFarm = useCallback(
    (value: number, isPerHectare: boolean) => {
      if (isPerHectare && projectAreaHa > 0 && Number.isFinite(value)) {
        return value / projectAreaHa;
      }
      return value;
    },
    [projectAreaHa]
  );

  // Outcome-based range
  const { min: uncappedOutcomeMin, max: uncappedOutcomeMax } = useMemo(
    () =>
      singleFieldOutcomes
        .map((field) => getFieldOutcomeBasedPayoutRange(field, reportingYear))
        .reduce<{ min: number; max: number }>(
          (acc, payoutRange) => ({
            min: acc.min + (payoutRange?.min ?? 0),
            max: acc.max + (payoutRange?.max ?? 0),
          }),
          { min: 0, max: 0 }
        ),
    [reportingYear, singleFieldOutcomes]
  );

  const practiceBasedIncentivesCurrentPayouts = useMemo(
    () => aggregatePracticeBasedIncentiveCurrentPayouts(singleFieldOutcomes, incentivesPotentialPayouts),
    [singleFieldOutcomes, incentivesPotentialPayouts]
  );

  const uncappedTotalPracticeBasedPayout = useMemo(
    () => Object.values(practiceBasedIncentivesCurrentPayouts).reduce((a, b) => a + b, 0),
    [practiceBasedIncentivesCurrentPayouts]
  );

  const totalPracticeBasedPayout =
    maximumPotentialPayout === null ? uncappedTotalPracticeBasedPayout : Math.min(uncappedTotalPracticeBasedPayout, maximumPotentialPayout);

  // Outcome-based payout is capped to the remaining budget after practice-based payouts, floored at 0 to prevent negative values
  // In the case that there is no maximum per-hectare (for example, Suntory),
  // set the maximum to infinitty to prevent capping it, and hide the "potential" columns in UI (implemented elsewhere)
  const totalOutcomePotentialPayout = maximumPotentialPayout === null ? Infinity : Math.max(0, maximumPotentialPayout - totalPracticeBasedPayout);

  const totalOutcomeBasedPayoutMin = Math.min(uncappedOutcomeMin, totalOutcomePotentialPayout);
  const totalOutcomeBasedPayoutMax = Math.min(uncappedOutcomeMax, totalOutcomePotentialPayout);

  let totalPayoutMin = 0;
  let totalPayoutMax = 0;

  if (carbonProgram?.programSlug === "suntory") {
    totalPayoutMin = Math.max(0, totalOutcomeBasedPayoutMin) + totalPracticeBasedPayout;
    totalPayoutMax = Math.max(0, totalOutcomeBasedPayoutMax) + totalPracticeBasedPayout;
  } else {
    totalPayoutMin = totalOutcomeBasedPayoutMin + totalPracticeBasedPayout;
    totalPayoutMax = totalOutcomeBasedPayoutMax + totalPracticeBasedPayout;
  }

  // you can't reach a maximum if it doesn't exist, hence false. This also hides the badges
  const isMaxTotalPayoutReached = maximumPotentialPayout !== null && totalPayoutMax >= maximumPotentialPayout;

  const isMaxOutcomePayoutReached = totalOutcomeBasedPayoutMax >= totalOutcomePotentialPayout;

  // hide panel when there are no outcomes or projectAreaHa is falsy (0, null, or undefined)
  const hideInventoryPayouts = !(singleFieldOutcomes.length && projectAreaHa > 0);

  // practice based payout

  const incentivesCurrentPayouts = useMemo(
    () => aggregateIncentiveCurrentPayouts(singleFieldOutcomes, incentivesPotentialPayouts),
    [singleFieldOutcomes, incentivesPotentialPayouts]
  );

  const allPracticesMaxedOut = useMemo(() => {
    if (!incentivesPotentialPayouts?.practicePotentialTotalPayout) return false;
    return Object.entries(incentivesCurrentPayouts).every(
      ([currentPayoutkey, currentPayoutValue]) =>
        incentivesPotentialPayouts?.practicePotentialIncentives[currentPayoutkey as IncentiveName]?.total <= currentPayoutValue
    );
  }, [incentivesCurrentPayouts, incentivesPotentialPayouts?.practicePotentialIncentives, incentivesPotentialPayouts?.practicePotentialTotalPayout]);

  const isPracticePotentialFullyUtilized = allPracticesMaxedOut || practicePotentialPayoutTotal <= uncappedTotalPracticeBasedPayout;

  return {
    projectAreaHa,
    maximumIncentivePerHectare,
    maximumPotentialPayout,
    totalOutcomeBasedPayoutMin,
    totalOutcomeBasedPayoutMax,
    totalPracticeBasedPayout,
    totalPayoutMin,
    totalPayoutMax,
    isMaxTotalPayoutReached,
    totalOutcomePotentialPayout,
    isMaxOutcomePayoutReached,
    hideInventoryPayouts,
    incentivesCurrentPayouts,
    allPracticesMaxedOut,
    isPracticePotentialFullyUtilized,
    practicePotentialPayoutTotal,
    incentivesPotentialPayouts,
    displayPerHectareOrTotalFarm,
  };
}
